import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft, CalendarClock, Plus, RefreshCw, Sparkles, Target, TrendingUp,
  Trash2, Loader2, CheckCircle2, Circle, Layers, Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { PremiumLockedFeature } from "@/components/PremiumLockedFeature";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import {
  listExamPlansFn, createExamPlanFn, refreshExamPlanFn,
  toggleExamTaskFn, deleteExamPlanFn, examStudyKitFn,
} from "@/lib/exam.functions";

export const Route = createFileRoute("/exam")({
  component: ExamPage,
  head: () => ({
    meta: [
      { title: "AI Exam Mode - Inquira" },
      { name: "description", content: "Enter your exam date and get a countdown, a tailored study schedule, practice questions, flashcards and readiness predictions." },
      { property: "og:title", content: "AI Exam Mode - Inquira" },
      { property: "og:description", content: "Countdown, tailored schedule and readiness predictions for your next exam." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.inquira.online/exam" },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/exam" }],
  }),
});

interface ExamTask {
  id: string; scheduled_for: string; task: string; topic: string | null;
  kind: string; estimated_minutes: number; completed: boolean;
}
interface ExamPlan {
  id: string; title: string; subject: string | null; exam_date: string; topics: string[];
  target_confidence: number; overview: string | null; readiness: number | null;
  confidence: number | null; pace: string | null; days_remaining: number;
  tasks: ExamTask[];
  history: Array<{ readiness: number; confidence: number; created_at: string }>;
}

const KIND_STYLE: Record<string, string> = {
  study: "bg-neon-blue/15 text-neon-blue",
  practice: "bg-neon-green/15 text-neon-green",
  quiz: "bg-neon-purple/15 text-neon-purple",
  flashcards: "bg-neon-yellow/15 text-neon-yellow",
  review: "bg-neon-pink/15 text-neon-pink",
  rest: "bg-muted text-ink-muted",
};

function ExamPage() {
  const { user, isLoading: authLoading, isPaid } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const listPlans = useServerFn(listExamPlansFn);
  const createPlan = useServerFn(createExamPlanFn);
  const refreshPlan = useServerFn(refreshExamPlanFn);
  const toggleTask = useServerFn(toggleExamTaskFn);
  const deletePlan = useServerFn(deleteExamPlanFn);
  const studyKit = useServerFn(examStudyKitFn);

  const [plans, setPlans] = useState<ExamPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [examDate, setExamDate] = useState("");
  const [topics, setTopics] = useState("");
  const [kit, setKit] = useState<{ topic: string; questions: Array<{ question: string; answer: string; difficulty: string }>; flashcards_saved: number } | null>(null);
  const [kitBusy, setKitBusy] = useState(false);
  const [openAnswers, setOpenAnswers] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPlans(undefined);
      setPlans(res.plans as ExamPlan[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load your exams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreate = async () => {
    if (!title.trim() || !examDate) {
      toast.error("Add an exam name and date");
      return;
    }
    setBusy(true);
    try {
      const res = await createPlan({
        data: {
          title: title.trim(),
          subject: subject.trim() || null,
          exam_date: examDate,
          topics: topics.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 25),
          target_confidence: 85,
        },
      });
      toast.success("Your exam plan is ready");
      if (res.weak_topics?.length) toast.message(`Watch these topics: ${res.weak_topics.slice(0, 3).join(", ")}`);
      setShowForm(false);
      setTitle(""); setSubject(""); setExamDate(""); setTopics("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not build your plan");
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async (planId: string) => {
    setBusy(true);
    try {
      await refreshPlan({ data: { plan_id: planId } });
      toast.success("Plan rebuilt around your progress");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not rebuild the plan");
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (task: ExamTask) => {
    setPlans((prev) =>
      prev.map((p) => ({ ...p, tasks: p.tasks.map((t) => (t.id === task.id ? { ...t, completed: !t.completed } : t)) })),
    );
    try {
      await toggleTask({ data: { task_id: task.id, completed: !task.completed } });
    } catch {
      toast.error("Could not save that");
      await load();
    }
  };

  const handleDelete = async (planId: string) => {
    try {
      await deletePlan({ data: { plan_id: planId } });
      setPlans((prev) => prev.filter((p) => p.id !== planId));
    } catch {
      toast.error("Could not remove that exam");
    }
  };

  const handleKit = async (planId: string, topic: string | null) => {
    setKitBusy(true);
    setKit(null);
    setOpenAnswers({});
    try {
      const res = await studyKit({ data: { plan_id: planId, topic, language: language.code } });
      setKit(res);
      if (res.flashcards_saved) toast.success(`${res.flashcards_saved} flashcards added to your deck`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not build revision material");
    } finally {
      setKitBusy(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-ink-muted" />
      </div>
    );
  }

  if (!isPaid) {
    return (
      <div className="min-h-screen px-4 py-8 max-w-2xl mx-auto">
        <BackLink />
        <PremiumLockedFeature
          feature="exam_mode"
          title="AI Exam Mode is a Premium feature"
          description="Add an exam date and your coach builds a countdown, a day-by-day schedule, practice questions, flashcards, weak-topic reviews and readiness predictions."
          icon={<Brain className="size-7 text-neon-pink" />}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-6 max-w-3xl mx-auto pb-24">
      <BackLink />

      <div className="mt-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-black text-ink flex items-center gap-2">
            <CalendarClock className="size-6 text-neon-purple" /> AI Exam Mode
          </h1>
          <p className="text-sm text-ink-muted">Countdowns, tailored schedules and readiness predictions.</p>
        </div>
        <Button size="sm" onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" /> Exam
        </Button>
      </div>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {showForm && (
        <div className="mt-4 rounded-2xl border border-rule bg-card p-4 space-y-3">
          <Input placeholder="Exam name (e.g. Calculus Midterm)" value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Subject" value={subject} maxLength={100} onChange={(e) => setSubject(e.target.value)} />
            <Input type="date" value={examDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExamDate(e.target.value)} />
          </div>
          <Input placeholder="Topics, comma separated" value={topics} onChange={(e) => setTopics(e.target.value)} />
          <Button className="w-full" onClick={handleCreate} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            Build my exam plan
          </Button>
        </div>
      )}

      {!plans.length && !showForm && (
        <div className="mt-8 rounded-2xl border border-dashed border-rule p-8 text-center">
          <CalendarClock className="size-8 mx-auto text-ink-muted" />
          <p className="mt-3 text-sm text-ink-muted">No exams yet. Add a date and your coach takes it from there.</p>
        </div>
      )}

      <div className="mt-6 space-y-6">
        {plans.map((plan) => {
          const done = plan.tasks.filter((t) => t.completed).length;
          const pct = plan.tasks.length ? Math.round((done / plan.tasks.length) * 100) : 0;
          const trend = plan.history.length > 1 ? plan.history[plan.history.length - 1].readiness - plan.history[0].readiness : 0;
          const today = new Date().toISOString().slice(0, 10);
          return (
            <div key={plan.id} className="rounded-2xl border border-rule bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-display font-black text-lg text-ink">{plan.title}</h2>
                  <p className="text-xs text-ink-muted">
                    {plan.subject ? `${plan.subject} · ` : ""}{new Date(`${plan.exam_date}T00:00:00`).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-display font-black text-neon-purple leading-none">
                    {Math.max(0, plan.days_remaining)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-ink-muted">
                    {plan.days_remaining === 1 ? "day left" : "days left"}
                  </div>
                </div>
              </div>

              {plan.overview && <p className="mt-3 text-sm text-ink">{plan.overview}</p>}

              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <Stat label="Readiness" value={`${plan.readiness ?? 0}%`} icon={Target} />
                <Stat label="Confidence" value={`${plan.confidence ?? 0}%`} icon={Brain} />
                <Stat label="Trend" value={`${trend > 0 ? "+" : ""}${trend}`} icon={TrendingUp} />
              </div>

              <div className="mt-4">
                <div className="flex justify-between text-xs text-ink-muted mb-1">
                  <span>Schedule progress</span><span>{done}/{plan.tasks.length}</span>
                </div>
                <Progress value={pct} />
              </div>

              <div className="mt-4 space-y-1.5 max-h-96 overflow-y-auto">
                {plan.tasks.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleToggle(task)}
                    className={`w-full text-left flex items-start gap-2 rounded-xl border border-rule px-3 py-2 hover:bg-rule/20 transition-colors ${task.completed ? "opacity-60" : ""} ${task.scheduled_for === today ? "ring-1 ring-neon-purple/50" : ""}`}
                  >
                    {task.completed ? <CheckCircle2 className="size-4 mt-0.5 text-neon-green shrink-0" /> : <Circle className="size-4 mt-0.5 text-ink-muted shrink-0" />}
                    <span className="flex-1">
                      <span className={`text-sm text-ink ${task.completed ? "line-through" : ""}`}>{task.task}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-ink-muted">
                        <span className={`px-1.5 py-0.5 rounded-full font-semibold ${KIND_STYLE[task.kind] ?? KIND_STYLE.study}`}>{task.kind}</span>
                        {task.topic && <span>{task.topic}</span>}
                        <span>· {new Date(`${task.scheduled_for}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                        <span>· {task.estimated_minutes} min</span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => handleRefresh(plan.id)} disabled={busy}>
                  <RefreshCw className={`size-4 ${busy ? "animate-spin" : ""}`} /> Rebuild plan
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleKit(plan.id, plan.topics?.[0] ?? null)} disabled={kitBusy}>
                  {kitBusy ? <Loader2 className="size-4 animate-spin" /> : <Layers className="size-4" />} Weak-topic drill
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(plan.id)}>
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>

              {plan.topics?.length > 1 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {plan.topics.map((t) => (
                    <button
                      key={t}
                      onClick={() => handleKit(plan.id, t)}
                      className="text-[11px] px-2 py-1 rounded-full border border-rule text-ink-muted hover:text-ink hover:bg-rule/30"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {kit && (
        <div className="mt-6 rounded-2xl border border-rule bg-card p-4">
          <h3 className="font-display font-black text-ink flex items-center gap-2">
            <Sparkles className="size-4 text-neon-yellow" /> Practice: {kit.topic}
          </h3>
          <p className="text-xs text-ink-muted mt-1">
            {kit.flashcards_saved} matching flashcards were added to your deck.{" "}
            <Link to="/flashcards" className="underline">Review them</Link>
          </p>
          <div className="mt-3 space-y-2">
            {kit.questions.map((q, i) => (
              <div key={i} className="rounded-xl border border-rule p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-ink">{q.question}</p>
                  <span className="text-[10px] uppercase text-ink-muted shrink-0">{q.difficulty}</span>
                </div>
                <button
                  className="mt-2 text-xs font-semibold text-neon-purple"
                  onClick={() => setOpenAnswers((p) => ({ ...p, [i]: !p[i] }))}
                >
                  {openAnswers[i] ? "Hide answer" : "Show answer"}
                </button>
                {openAnswers[i] && <p className="mt-2 text-sm text-ink-muted whitespace-pre-wrap">{q.answer}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/coach" className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink">
      <ArrowLeft className="size-4" /> Back to coach
    </Link>
  );
}

function Stat({ label, value, icon: Icon }: { label: string; value: string; icon: typeof Target }) {
  return (
    <div className="rounded-xl border border-rule p-2">
      <Icon className="size-4 mx-auto text-ink-muted" />
      <div className="mt-1 font-display font-black text-ink">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</div>
    </div>
  );
}